import { Component, computed, effect, input, Signal, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CwButton, CwTooltip } from '@bbraun/cortex-angular';
import { TranslateModule } from '@ngx-translate/core';
import { MAX_VALUE_FLOW_RATE, MAX_VALUE_VOLUME } from '~/app/classes/TherapyJobFormClass';
import { TherapyJobStatus } from '~/app/enums/TherapyJobStatus';
import { CarrierSolution } from '~/app/models/generated/carrierSolution';
import { TherapyJobV2 } from '~/app/models/TherapyJobV2';
import { Dialog } from '~/app/services/dialog.service';
import { DataTypeValidator } from '~/app/utils/DataTypeValidator';
import { DecimalPrecisionValidator } from '~/app/utils/decimalPrecisionValidator';
import { notClearableValidator } from '~/app/utils/notClearableValidator';
import { PositiveNumberValidator } from '~/app/utils/positiveNumberValidator';
import { AutocompleteComponent } from '../autocomplete/autocomplete.component';
import { InputComponent } from '../input/input.component';

type ParamsForm = FormGroup<{
  totalVolumeEdited: FormControl<number | null>;
  flowRatePlannedEdited: FormControl<number | null>;
  carrierEdited: FormControl<CarrierSolution | string | null>;
}>;

type FormValue = ParamsForm['value'];
type NormalizedValue = Pick<TherapyJobV2, 'totalVolumeEdited' | 'flowRatePlannedEdited' | 'carrierEdited'>;

enum SaveState {
  ENABLED = 'enabled', // changes are valid and savable
  DISABLED = 'disabled' // nothing changed (or reverted to original)
}

@Component({
  selector: 'gplus-params-edit-modal',
  imports: [CwButton, TranslateModule, CwTooltip, AutocompleteComponent, InputComponent],
  templateUrl: './params-edit-modal.component.html',
  styleUrl: './params-edit-modal.component.scss'
})
export class ParamsEditModalComponent {
  public dialog = input<Dialog>(null);

  public carrierList: Signal<Array<CarrierSolution>> = computed(() => {
    return this.dialog()?.data?.sessionData()?.carriers || [];
  });

  private readonly editableFields: Signal<{
    totalVolumeEdited: boolean;
    flowRatePlannedEdited: boolean;
    carrierEdited: boolean;
  }> = computed(() => {
    const job: TherapyJobV2 = this.dialog()?.data?.therapyJobSignal();
    if (!job) {
      return {
        totalVolumeEdited: false,
        flowRatePlannedEdited: false,
        carrierEdited: false
      };
    }
    const hasVolume = !!(job.totalVolumeEdited || job.totalVolumePrescribed);

    return {
      totalVolumeEdited:
        job.status === TherapyJobStatus.PRESCRIBED || (job.status === TherapyJobStatus.RUNNING && !hasVolume),
      flowRatePlannedEdited: job.status === TherapyJobStatus.PRESCRIBED || job.status === TherapyJobStatus.RUNNING,
      carrierEdited: job.status === TherapyJobStatus.PRESCRIBED && job.carrierPrescribed === undefined
    };
  });

  public form: WritableSignal<ParamsForm> = signal(
    new FormGroup({
      totalVolumeEdited: new FormControl<number | null>(null, [
        PositiveNumberValidator.isPositive(),
        Validators.max(MAX_VALUE_VOLUME),
        DataTypeValidator.isNumber(),
        DecimalPrecisionValidator.isInteger()
      ]),
      flowRatePlannedEdited: new FormControl<number | null>(null, [
        PositiveNumberValidator.isPositive(),
        Validators.max(MAX_VALUE_FLOW_RATE),
        DataTypeValidator.isNumber(),
        DecimalPrecisionValidator.isInteger()
      ]),
      carrierEdited: new FormControl<CarrierSolution | string | null>(null, [])
    })
  );

  private readonly baseline: WritableSignal<NormalizedValue> = signal(null);
  private readonly formValue: Signal<FormValue> = toSignal(this.form().valueChanges, {
    initialValue: this.form().getRawValue()
  });

  protected readonly SaveState = SaveState;

  public saveState: Signal<SaveState> = computed(() => {
    this.formValue(); // reactive trigger only: re-runs on each value change

    const original = this.baseline();
    if (!this.form() || !original) {
      return SaveState.DISABLED;
    }

    // Form-level errors (letters in a number field, below min, or a cleared value)
    if (this.form().invalid) {
      return SaveState.DISABLED;
    }

    // Read getRawValue() (includes disabled controls) to match how baseline was captured
    const now = this.normalize(this.form().getRawValue());

    const changed =
      now.totalVolumeEdited !== original.totalVolumeEdited ||
      now.flowRatePlannedEdited !== original.flowRatePlannedEdited ||
      now.carrierEdited !== original.carrierEdited;

    return changed ? SaveState.ENABLED : SaveState.DISABLED;
  });

  public saveTooltipKey: Signal<string> = computed(() =>
    this.saveState() === SaveState.DISABLED && this.form()?.invalid
      ? 'EditTherapyJobFixErrors'
      : 'EditTherapyJobNothingToSave'
  );

  private initialized = false;

  constructor() {
    effect(() => {
      if (this.dialog()?.data?.therapyJobSignal() && !this.initialized) {
        this.initFrom();
        this.initialized = true;
      }
    });
  }

  private initFrom() {
    const job = this.dialog().data.therapyJobSignal();

    if (!job) {
      console.warn('Params edition in ModalComponent: TherapyJob not found');
      return;
    }

    // Patch form
    const volume = job.totalVolumeEdited || job.totalVolumePrescribed || null;
    const flowRate = job.flowRatePlannedEdited || job.flowRatePlannedPrescribed || null;
    const carrier = job.carrierEdited || job.carrierPrescribed || null;

    const normalizedValues = { totalVolumeEdited: volume, flowRatePlannedEdited: flowRate, carrierEdited: carrier };

    this.form().patchValue(normalizedValues, { emitEvent: false });
    this.baseline.set(normalizedValues);

    // disable non-editable fields and set initial values
    const fields = this.editableFields();
    (Object.keys(fields) as Array<keyof typeof fields>).forEach((field) => {
      if (!fields[field]) {
        this.form().get(field).disable({ emitEvent: false });
      }

      // add validator
      this.form().get(field).addValidators(notClearableValidator(normalizedValues[field]));
    });
  }

  public closeDialog(): void {
    this.dialog().confirm.next(false);
  }

  public submit() {
    const patch: Partial<TherapyJobV2> = this.processPatch(this.form().getRawValue());

    if (Object.keys(patch).length === 0) {
      console.warn('Params edition in ModalComponent: No changes to submit');
      return;
    }

    this.dialog().confirm.next(patch);
  }

  private processPatch(value: FormValue): Partial<TherapyJobV2> {
    const now = this.normalize(value);
    const original = this.baseline();

    return (Object.keys(now) as Array<keyof NormalizedValue>).reduce<Partial<TherapyJobV2>>((patch, key) => {
      // Skip unchanged fields and never send null (BE cannot clear values)
      if (now[key] !== original[key] && now[key] !== null) {
        (patch[key] as NormalizedValue[typeof key]) = now[key];
      }
      return patch;
    }, {});
  }

  private normalize(value: FormValue): NormalizedValue {
    return {
      totalVolumeEdited: this.toNumberOrNull(value.totalVolumeEdited),
      flowRatePlannedEdited: this.toNumberOrNull(value.flowRatePlannedEdited),
      carrierEdited: this.toCarrierKey(value.carrierEdited)
    };
  }

  private toNumberOrNull(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const n = Number(value);
    return Number.isNaN(n) ? null : n;
  }

  private toCarrierKey(value: CarrierSolution | string | null | undefined): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    return typeof value === 'string' ? value : value.description || null;
  }
}
